import { CssBaseline, ThemeProvider } from '@mui/material';
import { MotionConfig } from 'motion/react';
import { FunctionComponent } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import './config/Firebase';
import { MindTheme } from './config/MindTheme';
import { AuthProvider } from './contexts/AuthContext';
import { Analytics } from './data/Analytics';
import { Errors } from './data/Errors';
import './Fonts';
import { Routes } from './Routes';
import { Sound } from './Sound';
import { gameConfigStore, useGameConfigSelector } from './store/GameConfigStore';
import { store, storePersistor } from './store/Store';
import './util/Arrays';
import { Environment } from './util/Environment';
import { Log } from './util/Logging';
import './util/Time';

function main() {
	Log.system.info(`Starting app v${Environment.appVersion}...`);
	void Analytics.initialize();
	Errors.initialize();
	Sound.initialize();
	initializeReact();
}

function initializeReact() {
	const container = document.getElementById('root');
	if (container) {
		const root = createRoot(container);
		root.render(<App />);
	}
	Log.system.info('React root initialized');
}

const App: FunctionComponent = () =>
	<Provider store={store}>
		<PersistGate loading={null} persistor={storePersistor}>
			<AppContent />
		</PersistGate>
	</Provider>;

const AppContent: FunctionComponent = () => {
	const animationsDisabled = useGameConfigSelector(gameConfigStore.selectors.sensorySensitivityMode);

	return <MotionConfig
		reducedMotion={animationsDisabled ? 'always' : 'never'}
		transition={animationsDisabled ? { duration: 0, delay: 0 } : undefined}
	>
		<AuthProvider>
			<ThemeProvider theme={MindTheme}>
				<CssBaseline />
				<Routes />
			</ThemeProvider>
		</AuthProvider>
	</MotionConfig>;
};

main();